import { isAiConfigUsageAllowed } from '@gonfalon/ai-configs/isAiConfigUsageAllowed';
import { ContextKindKey } from '@gonfalon/contexts/types';
import {
  aiConfigUsageGateTrials,
  enableAIBundle as enableAIConfigFlag,
  enableAIConfigWaitlistPage as enableAIConfigWaitlistPageFlag,
  enforceResourceNameLength,
} from '@gonfalon/dogfood-flags/dogfood-flags';
// eslint-disable-next-line no-restricted-imports
import { fromJS, List, Map, Record as ImmutableRecord, Set } from '@gonfalon/immutable-shim/immutableShim';
import { isLength, isNotEmpty, isReservedKey, validateRecord } from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { findIndexByKey } from '@gonfalon/legacy-immutable/immutableUtils';
import { trackAddTargetsToSegment } from '@gonfalon/segments/analytics';
import { SegmentType } from '@gonfalon/segments/types';
import { isValidKey } from '@gonfalon/strings/isValidKey';
import { v4 } from 'uuid';

import type { AiConfigEditorVariation } from '#components/AiConfigs/types';
import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks, CheckAccessFunction } from '#utils/access/types';
import type { Clause } from '#utils/clause/records/Clause';
import { createClause } from '#utils/clause/records/Clause';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';

import { SegmentVariationTypes } from './expiringContextTargetsUtils';

const MAX_AICONFIG_NAME_LENGTH = 256;
const MAX_AICONFIG_KEY_LENGTH = 256;

export const shouldShowAiConfigWaitlist = (): boolean => {
  const aiConfigLimits = aiConfigUsageGateTrials();
  return enableAIConfigWaitlistPageFlag() && !enableAIConfigFlag() && !isAiConfigUsageAllowed(aiConfigLimits);
};

const validateAiConfig = (aiConfig: AiConfig) =>
  validateRecord(
    aiConfig,
    isNotEmpty('name'),
    enforceResourceNameLength() ? isLength(1, MAX_AICONFIG_NAME_LENGTH)('name') : () => undefined,
    isNotEmpty('key'),
    isValidKey('key'),
    isReservedKey('key'),
    isLength(1, MAX_AICONFIG_KEY_LENGTH)('key'),
  );

export function createAiConfigForm(props = {}) {
  return props instanceof AiConfig ? props : new AiConfig(props);
}

type AiConfigRuleProps = {
  _key: string;
  _id: string;
  clauses: List<Clause>;
  weight?: number;
  rolloutContextKind?: string;
  bucketBy?: string;
  description?: string;
};

export class AiConfigRule extends ImmutableRecord<AiConfigRuleProps>({
  _key: '',
  _id: '',
  clauses: List.of(createClause()),
  weight: undefined,
  rolloutContextKind: ContextKindKey.USER,
  bucketBy: undefined,
  description: '',
}) {
  hasNonUserContextClause() {
    return this.clauses.some((c) => !!c.contextKind && c.contextKind !== ContextKindKey.USER);
  }
  hasNonUserContextRollout() {
    return !!this.rolloutContextKind && this.rolloutContextKind !== ContextKindKey.USER;
  }
  hasNestedAiConfigClause() {
    return this.clauses.some((c) => c.hasSegmentOp());
  }
}

type RelatedFlagProps = {
  _links: ImmutableMap<{
    parent: Link;
    self: Link;
  }>;
  _site: Link;
  key: string;
  name: string;
};

export class RelatedFlag extends ImmutableRecord<RelatedFlagProps>({
  _links: Map(),
  _site: Map(),
  key: '',
  name: '',
}) {
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  siteLink(): string {
    return this._site.get('href');
  }
}

type BigAiConfigTargetProps = {
  included: boolean;
  excluded: boolean;
};

export class BigAiConfigTarget extends ImmutableRecord<BigAiConfigTargetProps>({
  included: false,
  excluded: false,
}) {}

export function createBigAiConfigTarget(props = {}) {
  return props instanceof BigAiConfigTarget ? props : new BigAiConfigTarget(props);
}

type AiConfigTargetProps = {
  values: List<string>;
  contextKind: string;
};
export class AiConfigTarget extends ImmutableRecord<AiConfigTargetProps>({
  values: List(),
  contextKind: '',
}) {
  getContextKind() {
    return this.contextKind || ContextKindKey.USER;
  }
}

export type AiConfigProps = {
  _links: ImmutableMap<{
    parent: Link;
    self: Link;
    site: Link;
  }>;
  _access?: AccessChecks;
  _flags: List<RelatedFlag>;
  _flagCount: number;
  key: string;
  name: string;
  version: number;
  description: string;
  tags: Set<string>;
  creationDate: number;
  lastModifiedDate: number;
  rules: List<AiConfigRule>;
  included: List<string>;
  excluded: List<string>;
  includedContexts: List<AiConfigTarget>;
  excludedContexts: List<AiConfigTarget>;
  unbounded: boolean;
  unboundedContextKind?: string;
  _external: string;
  _externalLink: string;
  _unboundedMetadata: {
    version: number;
    includedCount: number;
    excludedCount: number;
    lastModified: number;
  };
  _importInProgress: boolean;
  type?: AiConfigProvisionType;
};

export class AiConfig extends ImmutableRecord<AiConfigProps>({
  _links: Map(),
  _access: undefined,
  _flags: List(),
  _flagCount: 0,
  name: '',
  key: '',
  version: 0,
  description: '',
  tags: Set(),
  creationDate: 0,
  lastModifiedDate: 0,
  rules: List(),
  included: List(),
  excluded: List(),
  includedContexts: List(),
  excludedContexts: List(),
  unbounded: false,
  unboundedContextKind: '',
  _external: '',
  _externalLink: '',
  _unboundedMetadata: {
    version: 0,
    includedCount: 0,
    excludedCount: 0,
    lastModified: 0,
  },
  _importInProgress: false,
}) {
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  siteLink() {
    return this._links.getIn(['site', 'href']);
  }

  checkAccess(profile: Member): CheckAccessFunction {
    const access = this._access;

    if (profile.isReader()) {
      return () =>
        createAccessDecision({
          isAllowed: false,
          appliedRoleName: 'Reader',
        });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  validate() {
    return validateAiConfig(this);
  }

  addRule(rule?: AiConfigRule, atIndex?: number) {
    if (atIndex !== undefined) {
      return this.update('rules', (rs) => rs.insert(atIndex, rule || createAiConfigRule()));
    }
    return this.update('rules', (rs) => rs.push(rule || createAiConfigRule()));
  }

  deleteRule(rule: AiConfigRule) {
    const index = findIndexByKey(this.rules, rule);

    if (index === -1) {
      return this;
    }

    return this.update('rules', (rs) => rs.delete(index));
  }

  addRuleClause(rule: AiConfigRule, clause?: Clause) {
    const index = findIndexByKey(this.rules, rule);

    if (index === -1) {
      return this;
    }

    const ruleClauses = this.rules.get(index)?.clauses;

    let contextKind: string = ContextKindKey.USER;

    if (!!ruleClauses && !ruleClauses.isEmpty()) {
      contextKind = ruleClauses.last<Clause>().contextKind;
    }

    return this.updateIn(['rules', index], (r) =>
      r.update('clauses', (cs: List<Clause>) => cs.push(clause || createClause({ contextKind }))),
    );
  }

  deleteRuleClause(rule: AiConfigRule, clause: Clause) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const clauseIndex = findIndexByKey(
      this.rules.get(ruleIndex)!.clauses,
      clause,
    ); /* eslint-enable @typescript-eslint/no-non-null-assertion */

    if (clauseIndex === -1) {
      return this;
    }

    return this.updateIn(['rules', ruleIndex], (r) =>
      r.update('clauses', (cs: List<Clause>) => cs.delete(clauseIndex)),
    );
  }

  editRuleClause(rule: AiConfigRule, clause: Clause) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const clauseIndex = findIndexByKey(
      this.rules.get(ruleIndex)!.clauses,
      clause,
    ); /* eslint-enable @typescript-eslint/no-non-null-assertion */

    if (clauseIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'clauses', clauseIndex], clause);
  }

  setRuleRollout(rule: AiConfigRule, weight: number) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'weight'], weight);
  }

  setRuleRolloutContextKind(rule: AiConfigRule, contextKind: string) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'rolloutContextKind'], contextKind);
  }

  setRuleBucket(rule: AiConfigRule, attr: string) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'bucketBy'], attr);
  }

  setRuleIndex(ruleKey: string, index: number) {
    return this.update('rules', (rules) => {
      const previousIndex = rules.findIndex((r) => r._key === ruleKey);
      if (previousIndex === -1 || index === -1) {
        return rules;
      }
      const rule = rules.get(previousIndex);
      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      return rules
        .delete(previousIndex)
        .insert(index, rule!); /* eslint-enable @typescript-eslint/no-non-null-assertion */
    });
  }

  setRuleDescription(rule: AiConfigRule, description: string) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'description'], description);
  }

  getTargetCount(aiConfigVariation: SegmentVariationTypes) {
    let targetValues = 0;
    if (aiConfigVariation === SegmentVariationTypes.INCLUDED) {
      this.includedContexts.forEach((target) => {
        targetValues += target.values.size;
      });
      targetValues += this.included.size;
    } else {
      this.excludedContexts.forEach((target) => {
        targetValues += target.values.size;
      });
      targetValues += this.excluded.size;
    }
    return targetValues;
  }

  hasTargetsOrRules() {
    return this.countAllTargets() > 0 || this.rules.size > 0;
  }

  setContextTarget(
    targets: List<string>,
    contextKind: string,
    index: number,
    field: 'includedContexts' | 'excludedContexts',
  ) {
    trackAddTargetsToSegment(contextKind);
    // delete the AiConfigTarget stored in the included or excluded context field if the list of targets has been cleared for a given context
    if (targets.isEmpty()) {
      if (index >= 0) {
        return this.deleteIn([field, index]);
      } else {
        return this;
      }
    }
    if (index >= 0) {
      // update the list of targets for the given context if it already exists
      return this.updateIn([field, index], (f) => f.set('values', targets));
    } else {
      // create a new AiConfigTarget in includedContexts if none already exists for a given context
      const target = new AiConfigTarget({ values: targets, contextKind });
      return this.update(field, (f) => f.push(target));
    }
  }

  getContextIndex(variation: 'includedContexts' | 'excludedContexts', contextKind: string) {
    return this[variation].findIndex((ic) => ic.contextKind === contextKind);
  }

  isTargetingNonUserContexts() {
    const hasNonUserTargets = !this.includedContexts.isEmpty() || !this.excludedContexts.isEmpty();
    const hasNonUserTargetingRules = this.rules.some(
      (r) => r.hasNonUserContextClause() || r.hasNonUserContextRollout(),
    );
    return hasNonUserTargets || hasNonUserTargetingRules;
  }

  hasNestedAiConfigRule() {
    return this.rules.some((r) => r.hasNestedAiConfigClause());
  }

  setIncludedTargets(targets: List<string>, contextKind: string) {
    // user context targets are stored in the included field
    if (contextKind === 'user') {
      trackAddTargetsToSegment(contextKind);
      return this.set('included', targets);
    }
    const index = this.getContextIndex('includedContexts', contextKind);
    return this.setContextTarget(targets, contextKind, index, 'includedContexts');
  }

  setExcludedTargets(targets: List<string>, contextKind: string) {
    // user context targets are stored in the excluded field
    if (contextKind === 'user') {
      trackAddTargetsToSegment(contextKind);
      return this.set('excluded', targets);
    }
    const index = this.getContextIndex('excludedContexts', contextKind);
    return this.setContextTarget(targets, contextKind, index, 'excludedContexts');
  }

  // Combines user and context targets
  getAllTargets(type: SegmentVariationTypes) {
    let contexts = type === SegmentVariationTypes.INCLUDED ? this.includedContexts : this.excludedContexts;
    const userIdx = contexts.findIndex((t) => t.contextKind === ContextKindKey.USER);
    const values = type === SegmentVariationTypes.INCLUDED ? this.included : this.excluded;
    const target = new AiConfigTarget({ values, contextKind: ContextKindKey.USER });
    if (userIdx > -1) {
      contexts = contexts.set(userIdx, target);
    } else {
      contexts = contexts.push(target);
    }
    return contexts;
  }

  countAllTargets() {
    if (this.unbounded) {
      const includedCount = this.getIn(['_unboundedMetadata', 'includedCount']);
      const excludedCount = this.getIn(['_unboundedMetadata', 'excludedCount']);
      return includedCount + excludedCount;
    }

    const includedCount = this.getTargetCount(SegmentVariationTypes.INCLUDED);
    const excludedCount = this.getTargetCount(SegmentVariationTypes.EXCLUDED);

    return includedCount + excludedCount;
  }

  countAllRules() {
    return this.rules.size;
  }

  countAllTargetsAndRules() {
    return this.countAllTargets() + this.countAllRules();
  }

  hasDependentFlags() {
    return this._flags.size > 0;
  }

  isBasic() {
    return !this.unbounded;
  }

  isBig() {
    return this.unbounded;
  }

  isNativeBig() {
    return this.isBig() && !this._external;
  }

  isSynced() {
    return this.isBig() && !!this._external;
  }

  updateBigAiConfigTargetCount({ addedCount, removedCount }: { addedCount: number; removedCount: number }) {
    if (!this.isNativeBig()) {
      return this;
    }
    const startingCount: number = this.getIn(['_unboundedMetadata', 'includedCount']);
    const updatedCount = startingCount + addedCount - removedCount;
    return this.setIn(['_unboundedMetadata', 'includedCount'], updatedCount);
  }

  isEmptyBigAiConfig() {
    if (!this.isNativeBig()) {
      return false;
    }
    const hasEmptyTargets = (this.getIn(['_unboundedMetadata', 'includedCount']) as number) === 0;
    return hasEmptyTargets && !this._importInProgress;
  }

  getType() {
    if (this.isSynced() && this._external === SegmentType.AMPLITUDE) {
      return SegmentType.AMPLITUDE;
    }

    if (this.isNativeBig()) {
      return SegmentType.NATIVE_BIG;
    }

    return SegmentType.REGULAR;
  }
}

export const getAiConfigTargetByContext = (targets: List<AiConfigTarget>, contextKind: string) => {
  const target = targets.find((t) => t.contextKind === contextKind);
  return target ? target.values : List();
};
export function createAiConfigRule(props = {}) {
  const rule = props instanceof AiConfigRule ? props : new AiConfigRule(props);
  return rule.update('_key', (key) => key || v4()).update('clauses', (cs) => cs.map(createClause));
}

export function createAiConfig(props = {}) {
  const aiConfig = props instanceof AiConfig ? props : new AiConfig(fromJS(props));
  return aiConfig
    .update('tags', (ts) => ts.toSet())
    .update('rules', (rs) => rs.map(createAiConfigRule))
    .update('includedContexts', (ic) => ic?.map((i) => new AiConfigTarget(i)))
    .update('excludedContexts', (ec) => ec?.map((e) => new AiConfigTarget(e)))
    .update('_flags', (fs: List<RelatedFlag>) => (fs ? fs.map((f) => new RelatedFlag(f)) : fs));
}

export enum AiConfigProvisionType {
  TEMPORARY = 'temporary',
  PERMANENT = 'permanent',
}

const bigAiConfigIndividualTargetUpdatesFields = {
  added: List(),
  removed: List(),
};

type BigAiConfigIndividualTargetUpdatesProps = {
  added: List<string>;
  removed: List<string>;
};
export type BigAiConfigIndividualTargetUpdatesField = keyof typeof bigAiConfigIndividualTargetUpdatesFields;
export class BigAiConfigIndividualTargetUpdates extends ImmutableRecord<BigAiConfigIndividualTargetUpdatesProps>(
  bigAiConfigIndividualTargetUpdatesFields,
) {
  updateTargetsList(field: BigAiConfigIndividualTargetUpdatesField, targetKey: string) {
    return this.update(field, (f) => f.push(targetKey));
  }
  getTargetUpdateField(targetKey: string): BigAiConfigIndividualTargetUpdatesField | undefined {
    if (this.added.includes(targetKey)) {
      return 'added';
    }
    if (this.removed.includes(targetKey)) {
      return 'removed';
    }
    return;
  }
  toRep() {
    return {
      included: {
        add: this.added.toArray(),
        remove: this.removed.toArray(),
      },
    };
  }
}

export function createBigAiConfigIndividualTargetUpdates(
  props: CreateFunctionInput<BigAiConfigIndividualTargetUpdatesProps> = {},
) {
  return props instanceof BigAiConfigIndividualTargetUpdates
    ? props
    : new BigAiConfigIndividualTargetUpdates(fromJS(props));
}

export enum BigAiConfigUploadMode {
  REPLACE = 'replace',
  MERGE = 'merge',
}

export enum AiConfigSourceType {
  RULES = 'rules',
  LIST = 'list',
  SYNC = 'sync',
}

export const getAiConfigSourceType = (source: string) => {
  switch (source) {
    case 'rules':
      return AiConfigSourceType.RULES;
    case 'list':
      return AiConfigSourceType.LIST;
    case 'sync':
      return AiConfigSourceType.SYNC;
    default:
      return null;
  }
};

export const rankAiConfigsByQueryMatch = (aiConfigs: List<AiConfig>, query: string): List<AiConfig> => {
  const normalizedQuery = query.toLowerCase().trim();

  return aiConfigs.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aKey = a.key.toLowerCase();
    const bKey = b.key.toLowerCase();

    // Exact matches get highest priority
    if (aName === normalizedQuery || aKey === normalizedQuery) {
      return -1;
    }
    if (bName === normalizedQuery || bKey === normalizedQuery) {
      return 1;
    }

    // Starts with query gets second priority
    if (
      (aName.startsWith(normalizedQuery) || aKey.startsWith(normalizedQuery)) &&
      !(bName.startsWith(normalizedQuery) || bKey.startsWith(normalizedQuery))
    ) {
      return -1;
    }
    if (
      (bName.startsWith(normalizedQuery) || bKey.startsWith(normalizedQuery)) &&
      !(aName.startsWith(normalizedQuery) || aKey.startsWith(normalizedQuery))
    ) {
      return 1;
    }

    // Contains query gets third priority
    const aContains = aName.includes(normalizedQuery) || aKey.includes(normalizedQuery);
    const bContains = bName.includes(normalizedQuery) || bKey.includes(normalizedQuery);
    if (aContains && !bContains) {
      return -1;
    }
    if (bContains && !aContains) {
      return 1;
    }

    // Fall back to alphabetical order by name
    return aName.localeCompare(bName);
  });
};

export const isDraftVariation = (variation: AiConfigEditorVariation) => !variation._id;
